import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";

// Fallback when logo skeleton has no width/height in json (avoids getLocalBounds which can throw)
const LOGO_NATIVE_WIDTH = 400;
const LOGO_NATIVE_HEIGHT = 200;

/**
 * Manages spine animation for Logo components
 * Handles initialization, scaling, and animation playback
 */
class LogoController {
  constructor(container, scaledWidth, scaledHeight, deviceConfig) {
    this.container = container;
    this.scaledWidth = scaledWidth;
    this.scaledHeight = scaledHeight;
    this.deviceConfig = deviceConfig;
    this.spine = null;
    this.isInitialized = false;
    this.desiredVisibility = false; // Store desired visibility state

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  initializeSpineAnimation() {
    this.spine = this.createSpine();
    if (!this.spine) {
      return;
    }

    if (
      !this.spine.skeleton ||
      !this.spine.state ||
      !this.spine.skeleton?.data
    ) {
      return;
    }

    this.container.addChild(this.spine);
    this.spine.position.set(0, 0);
    this.spine.zIndex = 5;
    this.spine.visible = true;
    this.spine.alpha = 0.001;
    this.spine.skeleton.setToSetupPose();

    if (this.spine.parent !== this.container) {
      return;
    }

    this.applySpineScaling();
    // Play logo_idle (logo spine) or idle, fallback to first available
    const animToPlay =
      this.spine.skeleton.data.animations.find((a) => a.name === "logo_idle") ||
      this.spine.skeleton.data.animations.find((a) => a.name === "idle") ||
      this.spine.skeleton.data.animations[0];
    if (animToPlay) {
      this.playAnimation(animToPlay.name, true);
    }
    this.isInitialized = true;
    this.spine.alpha = this.desiredVisibility ? 1 : 0.001;
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "logo-skeleton";
      const atlasAlias = "logo-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  playAnimation(animationName, loop = false) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(0, animationName, loop);
  }

  getNativeDimensions() {
    if (!this.spine?.skeleton?.data) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    const skeletonData = this.spine.skeleton.data;
    return {
      width: skeletonData.width || 0,
      height: skeletonData.height || 0,
      x: skeletonData.x || 0,
      y: skeletonData.y || 0,
    };
  }

  applySpineScaling() {
    if (!this.spine || !this.scaledWidth || !this.scaledHeight) {
      return;
    }

    // Same guard as IntroPopupController - spine must be in display tree
    if (!this.spine.scale || !this.spine.parent) {
      return;
    }

    if (!this.spine.skeleton || !this.spine.state) {
      return;
    }

    const nativeDims = this.getNativeDimensions();
    const width = nativeDims.width || LOGO_NATIVE_WIDTH;
    const height = nativeDims.height || LOGO_NATIVE_HEIGHT;

    if (width && height) {
      const scaleX = this.scaledWidth / width;
      const scaleY = this.scaledHeight / height;
      const scale = Math.min(scaleX, scaleY);

      try {
        this.spine.scale.set(scale, scale);
        this.spine.position.set(0, 0);
      } catch {
        // Silent fail like IntroPopupController
      }
      return;
    }

    // Fallback: use getLocalBounds only when spine is in tree (parent check above)
    try {
      const spineBounds = this.spine.getLocalBounds();
      if (spineBounds && spineBounds.width > 0 && spineBounds.height > 0) {
        const scaleX = this.scaledWidth / spineBounds.width;
        const scaleY = this.scaledHeight / spineBounds.height;
        const scale = Math.min(scaleX, scaleY);
        this.spine.scale.set(scale, scale);
        this.spine.position.set(0, 0);
      }
    } catch {
      // Silent fail - getLocalBounds can throw when spine not ready
    }
  }

  updateDimensions(newScaledWidth, newScaledHeight, newDeviceConfig) {
    const dimensionsChanged =
      this.scaledWidth !== newScaledWidth || this.scaledHeight !== newScaledHeight;

    this.scaledWidth = newScaledWidth;
    this.scaledHeight = newScaledHeight;
    if (newDeviceConfig) {
      this.deviceConfig = newDeviceConfig;
    }

    if (this.spine && this.isInitialized && dimensionsChanged) {
      this.applySpineScaling();
    }
  }

  setVisible(visible) {
    this.desiredVisibility = visible;
    if (this.spine && this.isInitialized) {
      this.spine.alpha = visible ? 1 : 0.001;
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }
    return this.spine.skeleton.data.animations.map((anim) => anim.name);
  }

  cleanup() {
    if (!this.spine) return;

    try {
      if (this.spine.state) {
        try {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        } catch {
          // Silent fail - state may already be destroyed
        }
      }
      if (this.container?.children?.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
    } catch {
      // Silent fail - spine/container may already be destroyed
    } finally {
      this.spine = null;
      this.isInitialized = false;
    }
  }
}

export default LogoController;
